import { Controller } from "@hotwired/stimulus";

const MIN_QUERY = 2;
const FOUND_CLASS = "settings-search-found";
const ACTIVE_CLASS = "settings-search-active";

// Searches the settings menus for pages and individual settings. Nothing
// changes until the query is two characters long; then matching settings
// list first, the first result is selected, ↑/↓ move, Enter opens and Esc
// clears. A result's link carries the setting's anchor, and on the page it
// opens the controller highlights that row.
export default class SettingsSearchController extends Controller {
  static targets = ["input", "result", "group", "empty"];

  declare readonly inputTarget: HTMLInputElement;
  declare readonly resultTargets: HTMLElement[];
  declare readonly groupTargets: HTMLElement[];
  declare readonly emptyTarget: HTMLElement;
  declare readonly hasEmptyTarget: boolean;

  private active = -1;

  connect() {
    this.revealLandedSetting();
  }

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase();
    const searching = query.length >= MIN_QUERY;

    this.resultTargets.forEach((result) => {
      const isSetting = result.dataset.kind === "setting";
      const matches = searching
        ? (result.dataset.terms ?? "").toLowerCase().includes(query)
        : !isSetting;
      result.classList.toggle("hidden", !matches);
      // A setting whose own name matches ranks above one found by its
      // search words alone.
      result.style.order = searching && !this.markLabel(result, query) ? "1" : "";
      if (!searching) this.markLabel(result, "");
    });

    this.groupTargets.forEach((group) => {
      const shown = this.resultTargets.some(
        (result) => group.contains(result) && !result.classList.contains("hidden"),
      );
      group.classList.toggle("hidden", !shown);
    });

    const visible = this.visibleResults();
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", !(searching && visible.length === 0));
    this.select(searching && visible.length > 0 ? 0 : -1);
  }

  navigate(event: KeyboardEvent) {
    const visible = this.visibleResults();

    if (event.key === "Escape") {
      this.inputTarget.value = "";
      this.filter();
    } else if (this.active < 0) {
      return;
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      this.select(Math.min(Math.max(this.active + step, 0), visible.length - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      visible[this.active]?.querySelector<HTMLAnchorElement>("a")?.click();
    }
  }

  // In the order they show: groups in document order, each group's results
  // by their rank.
  private visibleResults() {
    const rank = (result: HTMLElement) => Number(result.style.order || 0);
    return this.groupTargets.flatMap((group) =>
      this.resultTargets
        .filter((result) => group.contains(result) && !result.classList.contains("hidden"))
        .sort((a, b) => rank(a) - rank(b)),
    );
  }

  private select(index: number) {
    const visible = this.visibleResults();
    this.active = index;
    this.resultTargets.forEach((result) => result.classList.remove(ACTIVE_CLASS));
    const current = visible[index];
    if (!current) return;

    current.classList.add(ACTIVE_CLASS);
    current.scrollIntoView({ block: "nearest" });
  }

  // Wraps the matched part of a result's label in <mark>, from the label's
  // original text so repeated searches never nest. True when the label
  // itself matched.
  private markLabel(result: HTMLElement, query: string) {
    const label = result.querySelector<HTMLElement>("[data-settings-search-label]");
    if (!label) return false;

    const text = label.dataset.settingsSearchLabel ?? "";
    const at = query ? text.toLowerCase().indexOf(query) : -1;
    if (at < 0) {
      label.textContent = text;
      return false;
    }

    const mark = document.createElement("mark");
    mark.textContent = text.slice(at, at + query.length);
    label.replaceChildren(text.slice(0, at), mark, text.slice(at + query.length));
    return true;
  }

  // A result opens its page at #setting-<key>: open whatever hides the row
  // (a collapsed template, the Advanced details), bring it into view and
  // flag it for a moment.
  private revealLandedSetting() {
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = id ? document.getElementById(id) : null;
    if (!target || !target.closest("[data-settings-search-landing]")) return;

    target.closest("details")?.setAttribute("open", "");
    if (target.classList.contains("collapse")) target.classList.add("collapse-open");
    target.classList.add(FOUND_CLASS);
    target.scrollIntoView({ block: "center" });
    window.setTimeout(() => target.classList.remove(FOUND_CLASS), 2400);
  }
}
